import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { saveBinaryWithPicker } from "../flashtools/lib/saveAs";
import { Unlock, Lock, Upload, Download, Trash2, Terminal, FileSearch, KeyRound } from "lucide-react";
import { unlockXdf, bytesFromLatin1, xdfCandidatePasswords, getLearnedPasswords } from "../lib/xdfCrypto";
import { pullLearnedFromServer, pushLearnedToServer } from "../lib/xdfSync";

const hx = (v) => v.toString(16).padStart(2, "0").toUpperCase();
function shannonEntropy(bytes) {
  const c = new Array(256).fill(0);
  const n = bytes.length;
  for (let i = 0; i < n; i++) c[bytes[i]]++;
  let e = 0;
  for (const v of c) { if (v) { const p = v / n; e -= p * Math.log2(p); } }
  return e;
}
const count = (text, re) => (text.match(re) || []).length;

function parseDef(full) {
  const isAdx = /ADXFORMAT/i.test(full);
  const version =
    (full.match(/<XDFFORMAT[^>]*version="([^"]+)"/i) || [])[1] ||
    (full.match(/<ADXFORMAT[^>]*version="([^"]+)"/i) || [])[1] || "—";
  const title = (full.match(/<deftitle>([^<]*)<\/deftitle>/i) || [])[1] || "—";
  return {
    isAdx, version, title,
    counts: {
      tables: count(full, /<XDFTABLE[\s>]/gi),
      constants: count(full, /<XDFCONSTANT[\s>]/gi),
      flags: count(full, /<XDFFLAG[\s>]/gi),
      checksums: count(full, /<XDFCHECKSUM[\s>]/gi),
      patches: count(full, /<XDFPATCH[\s>]/gi),
      recorditems: count(full, /<RECORDITEM[\s>]/gi),
    },
  };
}

export default function XdfAdxToolkit() {
  return <XdfAdxToolkitInner />;
}

function XdfAdxToolkitInner() {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [size, setSize] = useState(0);
  const [info, setInfo] = useState(null);
  const [outText, setOutText] = useState(""); // downloadable unlocked XML
  const [viewText, setViewText] = useState("");
  const [log, setLog] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [password, setPassword] = useState("");
  const [pendingBytes, setPendingBytes] = useState(null);

  const addLog = (msg, type = "info") =>
    setLog((p) => [...p, { msg, type, t: new Date().toLocaleTimeString() }]);

  // Pull the account's saved passwords into the local store on mount (cross-device sync).
  useEffect(() => { pullLearnedFromServer(); }, []);

  const reset = () => {
    setFileName(""); setSize(0); setInfo(null); setOutText(""); setViewText(""); setLog([]); setPendingBytes(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const doDecrypt = (bytes) => {
    const candidates = xdfCandidatePasswords([password.trim(), password]);
    const learned = getLearnedPasswords().length;
    addLog(`— Decrypting (MD5 + RC4) with ${password ? `password "${password}" + ` : ""}auto candidates${learned ? ` (incl. ${learned} learned)` : ""} —`);
    const res = unlockXdf(bytes, candidates, addLog);
    if (res.success) {
      const d = parseDef(res.text);
      addLog(`Decryption complete — ${d.isAdx ? "ADX" : "XDF"} ${d.version} unlocked (password "${res.passwordUsed}")`, "ok");
      setViewText(res.text); setOutText(res.text);
      setInfo({
        status: "decrypted", format: d.isAdx ? "ADX" : "XDF", entropy: info?.entropy ?? 8, ...d,
        openPassword: res.openPassword, modifyPassword: res.modifyPassword, passwordUsed: res.passwordUsed,
      });
      toast.success(`Decrypted with password "${res.passwordUsed}"`);
      pushLearnedToServer();
    } else {
      setInfo((prev) => ({ ...(prev || {}), status: "locked", format: (fileName.toLowerCase().endsWith(".adx") ? "ADX" : "XDF"), entropy: prev?.entropy ?? 8 }));
      toast.error("Wrong password — enter the correct open password and retry");
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xdf") && !lower.endsWith(".adx")) {
      toast.error("Please select a .xdf or .adx file");
      return;
    }
    reset();
    setFileName(file.name);
    const ab = await file.arrayBuffer();
    const bytes = new Uint8Array(ab);
    setSize(bytes.length);
    addLog(`Loaded "${file.name}" — ${bytes.length.toLocaleString()} bytes`, "ok");

    const head = bytes.subarray(0, 4);
    const magicEnc = head[0] === 0x05 && head[1] === 0x22 && head[2] === 0x97;
    const entropy = shannonEntropy(bytes.subarray(0, Math.min(bytes.length, 262144)));
    const headText = new TextDecoder("latin1").decode(bytes.subarray(0, 8192));
    const looksXml =
      headText.replace(/^\uFEFF/, "").trimStart().startsWith("<") ||
      /XDFFORMAT|ADXFORMAT|<XDF|<ADX/i.test(headText);
    addLog(`Header bytes: ${Array.from(head).map(hx).join(" ")}`);
    addLog(`Shannon entropy: ${entropy.toFixed(3)} bits/byte`);

    // Already plain XML → unlocked
    if (looksXml && !magicEnc) {
      const full = new TextDecoder("utf-8").decode(bytes);
      const d = parseDef(full);
      addLog(`Detected UNLOCKED plain-text ${d.isAdx ? "ADX" : "XDF"} (${d.version})`, "ok");
      setViewText(full); setOutText(full);
      setInfo({ status: "plain", format: d.isAdx ? "ADX" : "XDF", entropy, ...d });
      toast.success("File already unlocked");
      return;
    }

    // Encrypted → attempt real decrypt
    addLog(`Detected ENCRYPTED / password-protected file`, "error");
    if (magicEnc) addLog(`Signature 05 22 97 (TunerPro protected XDF/ADX) found`);
    setPendingBytes(bytes);
    setInfo({ status: "locked", format: lower.endsWith(".adx") ? "ADX" : "XDF", entropy });
    doDecryptWith(bytes, entropy);
  };

  const doDecryptWith = (bytes, entropy) => {
    const candidates = xdfCandidatePasswords([password.trim(), password]);
    const learned = getLearnedPasswords().length;
    addLog(`— Decrypting (MD5 + RC4)${password ? ` with password "${password}" +` : " with"} auto candidates${learned ? ` (incl. ${learned} learned)` : ""} —`);
    const res = unlockXdf(bytes, candidates, addLog);
    if (res.success) {
      const d = parseDef(res.text);
      addLog(`Decryption complete — ${d.isAdx ? "ADX" : "XDF"} ${d.version} unlocked (password "${res.passwordUsed}")`, "ok");
      setViewText(res.text); setOutText(res.text);
      setInfo({
        status: "decrypted", format: d.isAdx ? "ADX" : "XDF", entropy, ...d,
        openPassword: res.openPassword, modifyPassword: res.modifyPassword, passwordUsed: res.passwordUsed,
      });
      toast.success(`Decrypted with password "${res.passwordUsed}"`);
      pushLearnedToServer();
    } else {
      setOutText(""); setViewText("");
      setInfo({ status: "locked", format: fileName.toLowerCase().endsWith(".adx") ? "ADX" : "XDF", entropy });
      toast.error("Could not decrypt — enter the correct open password and retry");
    }
  };

  const download = async () => {
    if (!outText) return;
    const base = fileName.replace(/\.(xdf|adx)$/i, "");
    const ext = fileName.toLowerCase().endsWith(".adx") ? ".adx" : ".xdf";
    const ok = await saveBinaryWithPicker(bytesFromLatin1(outText), `${base}_unlock${ext}`, { toast });
    if (ok) addLog(`Saved ${base}_unlock${ext}`, "ok");
  };

  const preview = useMemo(() => (viewText ? viewText.split("\n").slice(0, 4000).join("\n") : ""), [viewText]);
  const decrypted = info?.status === "decrypted";
  const plain = info?.status === "plain";
  const locked = info?.status === "locked";
  const readable = decrypted || plain;

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><FileSearch className="h-4 w-4" /> Definition Toolkit</div>
        <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">XDF / ADX Unlocker</h1>
        <p className="text-slate-500 text-sm mt-1">Decrypt password-protected XDF/ADX, inspect definitions, and download the unlocked file.</p>
      </div>

      <div
        data-testid="xdf-tool-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
        className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragging ? "border-cyan-400 bg-cyan-400/5" : "border-slate-700 bg-[#0c1017]"}`}
      >
        <input ref={inputRef} data-testid="xdf-tool-file-input" type="file" accept=".xdf,.adx" className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])} />
        <Upload className="h-8 w-8 text-slate-500 mx-auto mb-3" />
        <p className="text-slate-300 font-semibold">Drop your <span className="font-mono-tt text-cyan-300">.xdf</span> / <span className="font-mono-tt text-cyan-300">.adx</span> file here</p>
        <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
          <button data-testid="xdf-tool-browse-btn" onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold transition-colors">
            <Upload className="h-4 w-4" /> Browse .xdf / .adx
          </button>
          {info && (
            <button data-testid="xdf-tool-clear-btn" onClick={reset}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold">
              <Trash2 className="h-4 w-4" /> Clear
            </button>
          )}
        </div>
      </div>

      {info && (
        <div className="mt-6">
          <div className="space-y-5">
            <div data-testid="xdf-tool-status" className={`rounded-xl border p-5 ${locked ? "border-red-700/50 bg-red-950/20" : "border-emerald-700/50 bg-emerald-950/20"}`}>
              <div className="flex items-center gap-3">
                {locked ? <Lock className="h-7 w-7 text-cyan-300" /> : <Unlock className="h-7 w-7 text-emerald-400" />}
                <div>
                  <div className={`font-head font-black text-2xl uppercase ${locked ? "text-cyan-300" : "text-emerald-400"}`}>
                    {decrypted ? "Decrypted · Password Removed" : plain ? "Already Unlocked" : "Encrypted / Locked"}
                  </div>
                  <div className="text-xs text-slate-400">
                    {decrypted ? "Password protection stripped — download the unlocked file below."
                      : plain ? `Readable ${info.format} — no password protection.`
                      : "Enter the open password below (e.g. a dot \".\") and click Unlock."}
                  </div>
                </div>
              </div>

              <dl className="grid sm:grid-cols-3 gap-x-6 gap-y-3 text-sm font-mono-tt mt-5">
                <Row label="File name" value={fileName} />
                <Row label="File size" value={`${size.toLocaleString()} B`} />
                <Row label="Format" value={info.format} accent />
                <Row label="Entropy" value={`${info.entropy.toFixed(3)} bits/byte`} />
                {readable && <Row label="Version" value={info.version} />}
                {readable && <Row label="Title" value={info.title} />}
                {locked && <Row label="Header (16B)" value={info.magic} />}
              </dl>

              {locked && (
                <div className="mt-4">
                  <label className="text-[10px] uppercase tracking-widest text-slate-500">Open Password</label>
                  <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
                    <input data-testid="xdf-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder='e.g.  .  (dot)  or the file password'
                      className="flex-1 h-10 rounded-md bg-[#121824] border border-slate-700 text-slate-100 px-3 text-sm font-mono-tt focus:outline-none focus:ring-2 focus:ring-cyan-400" />
                    <button data-testid="xdf-unlock-btn" onClick={() => pendingBytes && doDecryptWith(pendingBytes, info.entropy)}
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold">
                      <Unlock className="h-4 w-4" /> Unlock with Password
                    </button>
                  </div>
                </div>
              )}

              {decrypted && info.passwordUsed !== undefined && (
                <div className="mt-4 rounded-lg bg-[#121824] border border-slate-700 p-3 inline-block">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><KeyRound className="h-3 w-3" /> Password Used</div>
                  <div data-testid="xdf-password-used" className="mt-1 font-mono-tt text-cyan-300 font-bold break-all">{info.passwordUsed === "" ? "(empty)" : info.passwordUsed}</div>
                </div>
              )}

              {decrypted && (info.openPassword || info.modifyPassword) && (
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <div className="rounded-lg bg-[#121824] border border-slate-700 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><KeyRound className="h-3 w-3" /> Recovered Open Password</div>
                    <div data-testid="xdf-open-password" className="mt-1 font-mono-tt text-cyan-300 font-bold break-all">{info.openPassword || "—"}</div>
                  </div>
                  <div className="rounded-lg bg-[#121824] border border-slate-700 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><KeyRound className="h-3 w-3" /> Recovered Modify Password</div>
                    <div data-testid="xdf-modify-password" className="mt-1 font-mono-tt text-cyan-300 font-bold break-all">{info.modifyPassword || "—"}</div>
                  </div>
                </div>
              )}
            </div>

            {readable && (
              <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-5">
                <h2 className="font-head font-bold text-xl uppercase text-white mb-4">Definition Contents</h2>
                {info.format === "ADX" ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <Stat testid="xdf-count-recorditems" label="Record Items" value={info.counts.recorditems} />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <Stat testid="xdf-count-tables" label="Tables" value={info.counts.tables} />
                    <Stat testid="xdf-count-constants" label="Constants" value={info.counts.constants} />
                    <Stat testid="xdf-count-flags" label="Flags" value={info.counts.flags} />
                    <Stat testid="xdf-count-checksums" label="Checksums" value={info.counts.checksums} />
                    <Stat testid="xdf-count-patches" label="Patches" value={info.counts.patches} />
                  </div>
                )}
                <button data-testid="xdf-tool-download-btn" onClick={download}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold">
                  <Download className="h-4 w-4" /> Download {decrypted ? "Unlocked" : "Readable"} {info.format}
                </button>
              </div>
            )}

            {readable && (
              <div className="rounded-xl border border-slate-800 bg-[#07090e] overflow-hidden">
                <div className="px-4 py-2.5 bg-[#121824] border-b border-slate-800 flex items-center gap-2">
                  <span className="font-mono-tt text-xs uppercase tracking-wider text-cyan-300">XML Viewer</span>
                </div>
                <pre data-testid="xdf-tool-viewer" className="overflow-auto max-h-[420px] font-mono-tt text-[12px] text-slate-300 p-3 whitespace-pre-wrap break-words">{preview}{viewText.split("\n").length > 4000 ? "\n… (truncated preview — full file in download)" : ""}</pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-slate-500">{label}</dt>
      <dd className={`mt-0.5 break-all ${accent ? "text-cyan-300 font-bold" : "text-slate-200"}`}>{value || "—"}</dd>
    </div>
  );
}
function Stat({ label, value, testid }) {
  return (
    <div className="rounded-lg bg-[#121824] border border-slate-800 p-3 text-center">
      <div data-testid={testid} className="font-head font-black text-3xl text-cyan-300">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}
